import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import EmployeeManagement from './EmployeeManagement';
import { PERSONNEL_STATUS_LEGEND, resolvePersonnelStatus } from './personnelStatus';

export default function PersonalManagement({ token, isDarkMode, companyId, canEditExpedientes = false, canManageEmployees = false }) {
  const [view, setView] = useState('directory');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const colors = {
    card: isDarkMode ? '#111827' : '#fff',
    text: isDarkMode ? '#f8fafc' : '#1f2937',
    muted: isDarkMode ? '#94a3b8' : '#718096',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    input: isDarkMode ? '#0f172a' : '#fff'
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = companyId ? { companyId } : {};
      const [response, attendanceResponse, leaveResponse, scheduleResponse] = await Promise.all([
        axios.get('/api/employee-management', { headers: { Authorization: `Bearer ${token}` }, params }),
        axios.get('/api/users/all-with-attendance', { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get('/api/leave-management', { headers: { Authorization: `Bearer ${token}` }, params }).catch(() => ({ data: [] })),
        axios.get('/api/work-schedules/assignments', { headers: { Authorization: `Bearer ${token}` }, params }).catch(() => ({ data: [] }))
      ]);

      const baseUsers = Array.isArray(response.data) ? response.data.filter(user => user.role !== 'SuperAdministrador') : [];
      const attendanceUsers = Array.isArray(attendanceResponse.data) ? attendanceResponse.data : [];
      const leaveRows = Array.isArray(leaveResponse.data) ? leaveResponse.data : [];
      const assignments = Array.isArray(scheduleResponse.data) ? scheduleResponse.data : [];
      const attendanceMap = new Map(attendanceUsers.map(user => [String(user.id), user]));
      // La plantilla debe utilizar la asignación de jornada efectiva hoy,
      // no simplemente la asignación con la fecha de inicio más reciente.
      const hoyMerida = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Merida',
        year: 'numeric', month: '2-digit', day: '2-digit'
      }).format(new Date());
      const assignmentMap = new Map();
      assignments
        .filter(item => String(item.status || 'ACTIVO').toUpperCase() === 'ACTIVO')
        .filter(item => String(item.startDate || '').slice(0, 10) <= hoyMerida)
        .filter(item => !item.endDate || String(item.endDate).slice(0, 10) >= hoyMerida)
        .forEach(item => {
          const key = String(item.userId);
          const current = assignmentMap.get(key);
          const itemPriority = Number(item.priority || 1);
          const currentPriority = Number(current?.priority || 1);
          const itemStart = String(item.startDate || '').slice(0, 10);
          const currentStart = String(current?.startDate || '').slice(0, 10);
          if (!current || itemPriority < currentPriority ||
              (itemPriority === currentPriority && itemStart > currentStart) ||
              (itemPriority === currentPriority && itemStart === currentStart && Number(item.id || 0) > Number(current.id || 0))) {
            assignmentMap.set(key, item);
          }
        });

      const enriched = baseUsers.map(user => ({
        ...user,
        checkins: attendanceMap.get(String(user.id))?.checkins || user.checkins || [],
        activeLeaves: leaveRows.filter(row => String(row.userId) === String(user.id) && String(row.status || '').toUpperCase() === 'APROBADA'),
        scheduleAssignment: assignmentMap.get(String(user.id)) || null
      }));

      setUsers(enriched);
      setMessage('');
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible cargar la plantilla de la empresa.');
    } finally {
      setLoading(false);
    }
  }, [token, companyId]);

  useEffect(() => { load(); }, [load]);

  const directory = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter(user => !q || [user.name, user.email, user.id, user.role].map(v => String(v ?? '').toLowerCase()).join(' ').includes(q))
      .sort((a, b) => {
        const roleA = a.role === 'Empleado' ? 0 : 1;
        const roleB = b.role === 'Empleado' ? 0 : 1;
        if (roleA !== roleB) return roleA - roleB;
        return String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' });
      });
  }, [users, search]);

  const employeeCount = users.filter(user => user.role === 'Empleado').length;
  const adminCount = users.filter(user => user.role !== 'Empleado').length;

  return (
    <section style={{ background: colors.card, color: colors.text, padding: 20, borderRadius: 12, border: `1px solid ${colors.border}`, marginBottom: 30 }}>
      <div style={{ marginBottom: 15 }}>
        <h2 style={{ margin: 0, fontSize: 21 }}>👥 Capital Humano</h2>
        <p style={{ margin: '5px 0 0', color: colors.muted, fontSize: 13 }}>
          La plantilla reúne empleados y administradores. Los expedientes laborales se gestionan aquí, mientras las cuentas, permisos y seguridad permanecen separadas.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 15 }}>
        <button type="button" onClick={() => setView('directory')} style={{ padding: '9px 13px', borderRadius: 8, border: `1px solid ${view === 'directory' ? '#3182ce' : colors.border}`, background: view === 'directory' ? '#3182ce' : (isDarkMode ? '#172033' : '#f8fafc'), color: view === 'directory' ? '#fff' : colors.text, fontWeight: 700, cursor: 'pointer' }}>👥 Plantilla</button>
        <button type="button" onClick={() => { setView('employees'); }} style={{ padding: '9px 13px', borderRadius: 8, border: `1px solid ${view === 'employees' ? '#3182ce' : colors.border}`, background: view === 'employees' ? '#3182ce' : (isDarkMode ? '#172033' : '#f8fafc'), color: view === 'employees' ? '#fff' : colors.text, fontWeight: 700, cursor: 'pointer' }}>📁 Expedientes</button>
      </div>

      {view === 'directory' ? (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔎 Buscar por nombre, correo, ID o rol..." style={{ flex: 1, minWidth: 240, padding: 9, borderRadius: 7, border: `1px solid ${colors.border}`, background: colors.input, color: colors.text }} />
            <span style={{ fontSize: 12, color: colors.muted }}>{employeeCount} empleados · {adminCount} administradores</span>
          </div>

          {message && <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, border: `1px solid ${colors.border}` }}>{message}</div>}

          {loading ? <div style={{ padding: 20, textAlign: 'center', color: colors.muted }}>Cargando plantilla…</div> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(270px,1fr))', gap: 10 }}>
              {directory.map(user => {
                const personnelStatus = resolvePersonnelStatus(user);
                return (
                  <div key={user.id} style={{ padding: 12, borderRadius: 9, border: `1px solid ${colors.border}`, background: isDarkMode ? '#131924' : '#f8fafc' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0 }}>
                        <strong style={{ display: 'block', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</strong>
                        <span style={{ display: 'block', marginTop: 3, fontSize: 10, color: colors.muted }}>ID {user.id} · {user.role}</span>
                        <span style={{ display: 'block', marginTop: 3, fontSize: 10, color: colors.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</span>
                      </div>
                      <span style={{ flexShrink: 0, padding: '4px 7px', borderRadius: 5, fontSize: 9, fontWeight: 700, background: user.role === 'Empleado' ? '#e6fffa' : '#ebf8ff', color: user.role === 'Empleado' ? '#2f855a' : '#2b6cb0' }}>{user.role === 'Empleado' ? 'EMPLEADO' : 'ADMINISTRADOR'}</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 11, padding: '7px 9px', borderRadius: 7, background: isDarkMode ? '#0f172a' : '#fff', border: `1px solid ${colors.border}` }}>
                      <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: '50%', background: personnelStatus.color, border: personnelStatus.key === 'suspendido' ? '1px solid #475569' : '1px solid rgba(0,0,0,.08)', boxShadow: personnelStatus.key === 'laborando' ? '0 0 0 3px rgba(34,197,94,.15)' : 'none', flexShrink: 0 }} />
                      <strong style={{ fontSize: 11, color: personnelStatus.color === '#000000' ? colors.text : personnelStatus.color }}>{personnelStatus.label}</strong>
                      <span style={{ marginLeft: 'auto', fontSize: 9, color: colors.muted }}>Estado actual</span>
                    </div>

                    <div style={{ marginTop: 5, fontSize: 10, color: colors.muted }}>{personnelStatus.detail}</div>

                    <div style={{ display: 'flex', gap: 7, marginTop: 10 }}>
                      <button type="button" onClick={() => { setSelectedUserId(String(user.id)); setView('employees'); }} style={{ flex: 1, padding: '7px 9px', border: 0, borderRadius: 7, background: '#3182ce', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>📁 Expediente</button>
                      <button type="button" onClick={() => setSelectedUserId(String(user.id))} style={{ padding: '7px 9px', border: `1px solid ${colors.border}`, borderRadius: 7, background: colors.card, color: colors.text, fontWeight: 700, cursor: 'pointer' }}>👤 Seleccionar</button>
                    </div>
                  </div>
                );
              })}
              {!directory.length && <div style={{ gridColumn: '1 / -1', padding: 25, textAlign: 'center', color: colors.muted }}>No hay usuarios que coincidan con la búsqueda.</div>}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 14, padding: '9px 11px', borderRadius: 8, border: `1px solid ${colors.border}`, background: isDarkMode ? '#0f172a' : '#f8fafc' }}>
            {PERSONNEL_STATUS_LEGEND.map(item => (
              <span key={item.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, color: colors.muted }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: item.color, border: item.key === 'suspendido' ? '1px solid #475569' : '1px solid rgba(0,0,0,.08)' }} />
                {item.label}
              </span>
            ))}
          </div>

          <div style={{ marginTop: 14, padding: 11, borderRadius: 8, border: `1px solid ${colors.border}`, color: colors.muted, fontSize: 11 }}>
            La edición de expedientes se realiza en <strong>Expedientes</strong>. Las cuentas administrativas, su jerarquía, alcance y permisos se administran en <strong>Administración avanzada</strong>.
          </div>
        </>
      ) : (
        <EmployeeManagement token={token} isDarkMode={isDarkMode} companyId={companyId} initialSelectedUserId={selectedUserId} canEditExpedientes={canEditExpedientes} canManageEmployees={canManageEmployees} />
      )}
    </section>
  );
}
